import React, { useMemo, useState } from 'react';
import {
	NavigationStepperComponentParams,
	NavigationStepperItemType,
} from '../../../../Components/Stepper/NavigationStepper';
import TabNavigationStepper from '../../../../Components/Stepper/TabNavigationStepper/TabNavigationStepper';
import { useEffectAsync } from '../../../../helpers';
import { useAppContext } from '../../../../Services/AppContext';
import { TreatmentContextProvider } from '../../../../Services/TreatmentContext';
import { useAclContext } from '../../../Acl/Components/AclContextProvider';
import {
	getActiveTreatmentTabForPatient,
	setActiveTreatmentTabForPatient,
} from '../../../Treatment/Services/treatment.service';
import PatientTreatmentCalendar from './Calendar';
import PatientTreatmentContextProvider from './ContextProvider';
import PatientTreatmentDocumentation from './Documentation';
import PatientTreatmentHealthPlan from './HealthPlan';
import PatientTreatmentPrescription from './Prescription';
import PatientTreatmentProfile from './Profile';
import PatientTreatmentTreatment from './Treatment';
import useWorkplaceRole from '../../../Workplace/Hooks/useWorkplaceRole';
import {
	useGetUserOriginalIdQuery,
	useLazyIsMyCaseQuery,
	useWorkplaceHealthPlansQuery,
} from '../../../../graphql/graphql';
import Loading from '../../../../Components/Loading';
import PatientTreatmentElectronicHealthCard from './ElectronicHealthCard';
import { NCZIContextProvider } from '../../../Nczi/Services/NCZIContext';
import { NCZICommandContextProvider } from '../../../Nczi/Services/NCZICommandContext';
import ElectronicExamination from './ElectronicExamination';
import ElectronicVaccination from './ElectronicVaccination';
import ElectronicDPN from './ElectronicDPN';
import ElectronicPrescription from './ElectronicPrescription';
import NCZIWizard from '../../../Nczi/Components/NCZIWizard/NCZIWizard';
import PatientAutoVerify from '../../../Nczi/Components/PatientAutoVerify/PatientAutoVerify';
import { useDispatch } from 'react-redux';
import {
	setAclLoaded,
	setAclLoading,
} from '../../../../store/reducers/aclSlice';
import ScrollContainer from '../../../Base/Components/ScrollContainer';
import { ECardType } from '../../../Ecard/Enums/ECardType.enum';
import { PatientDataContextProvider } from '../../Services/PatientDataContext';
import { InitialTabParams } from '../../Interfaces/InitialTabParams.interface';
import PatientTreatmentServiceRequests from './ServiceRequests';
import PatientTreatmentTemporaryStorage from './TemporaryStorage';
import useMzcrAccess from '../../../Mzcr/Services/useMzcrAccess';
import { HealthDocumentationFileRef } from '../../../Ecard/Interfaces/HealthDocumentationFileRef.interface';

interface PatientTreatmentProps {
	patientId: string;
	workplaceId?: string;
	caseId?: string;
	activeTab?: string;
	activeTabParams?: InitialTabParams;
	caseJustAssigned?: boolean;
}

const PatientTreatment: React.FC<PatientTreatmentProps> = props => {
	const { patientId, caseId, activeTab, activeTabParams, caseJustAssigned } =
		props;
	const appContext = useAppContext();
	const workplaceId = props.workplaceId ?? appContext.workplaceId;
	const { isAllowed } = useAclContext();
	const mzcrAccess = useMzcrAccess();
	const [steps, setSteps] = useState<NavigationStepperItemType[]>([]);
	// Files sent from Zdravotnická dokumentace, waiting for Dočasné úložiště → Nová zásilka (Z1/Z2)
	const [filesToSend, setFilesToSend] = useState<
		HealthDocumentationFileRef[] | null
	>(null);
	const { userData } = appContext;
	const userId = userData.id;
	const { isNurse } = useWorkplaceRole({ userId, workplaceId });
	const { data, isLoading } = useWorkplaceHealthPlansQuery();
	const originalUser = useGetUserOriginalIdQuery({ id: patientId });
	const dispatch = useDispatch();
	const patientsOriginalId = originalUser?.data?.user?.originalId;
	const [isMyCase, setIsMyCase] = useState<boolean>(false);
	const [isMyCaseIsLoading, setIsMyCaseIsLoading] = useState<boolean>(false);
	const [getIsMyCase] = useLazyIsMyCaseQuery();

	useEffectAsync(async () => {
		if (caseId) {
			setIsMyCaseIsLoading(true);
			const data = await getIsMyCase({ id: caseId });
			setIsMyCase(data?.data?.case?.isMyCase ?? false);
			setIsMyCaseIsLoading(false);
		}
	}, [caseId]);

	useEffectAsync(async () => {
		const tempSteps: NavigationStepperItemType[] = [
			{
				name: 'patientTreatment.profile.title',
				key: 'profile',
				component: (params: NavigationStepperComponentParams) => (
					<PatientTreatmentProfile workplaceId={workplaceId} />
				),
			},
		];

		if (isAllowed('patient_treatment')) {
			tempSteps.push({
				name: 'patientTreatment.cure.title',
				key: 'cure',
				component: (params: NavigationStepperComponentParams) => (
					<PatientTreatmentTreatment workplaceId={workplaceId} />
				),
			});
		}

		if (isAllowed('erecept')) {
			tempSteps.push({
				name: 'patientTreatment.prescription.title',
				key: 'prescription',
				component: (params: NavigationStepperComponentParams) => (
					<PatientTreatmentPrescription />
				),
			});
		}

		// eŽádanky (MZČR) right after eRecept
		if (mzcrAccess.isVisible) {
			tempSteps.push({
				name: 'patientTreatment.serviceRequests.title',
				key: 'serviceRequests',
				component: (params: NavigationStepperComponentParams) => (
					<PatientTreatmentServiceRequests
						onOpenProfile={() => params.setActiveStepPath('profile')}
					/>
				),
			});
		}

		tempSteps.push({
			name: 'patientTreatment.orders.title',
			key: 'orders',
			component: (params: NavigationStepperComponentParams) => (
				<PatientTreatmentCalendar />
			),
		});

		if (isAllowed('documentation')) {
			if (patientsOriginalId) {
				tempSteps.push({
					name: 'patientTreatment.eCard.title',
					key: 'eCard',
					component: (params: NavigationStepperComponentParams) => (
						<PatientTreatmentDocumentation
							type={ECardType.PATIENT_E_CARD}
							patientsOriginalId={patientsOriginalId}
						/>
					),
				});
			}

			// Dočasné úložiště (MZČR) between eKarta and Zdravotnická dokumentace
			if (mzcrAccess.isVisible) {
				tempSteps.push({
					name: 'patientTreatment.temporaryStorage.title',
					key: 'temporaryStorage',
					component: (params: NavigationStepperComponentParams) => (
						<PatientTreatmentTemporaryStorage
							onOpenProfile={() => params.setActiveStepPath('profile')}
						/>
					),
				});
			}

			tempSteps.push({
				name: 'patientTreatment.eCardHealthDocumentation.title',
				key: 'eCardHealthDocumentation',
				component: (params: NavigationStepperComponentParams) => (
					<PatientTreatmentDocumentation
						type={ECardType.HEALTH_DOCUMENTATION}
						patientsOriginalId={patientsOriginalId}
						onSendFiles={
							mzcrAccess.hasServices
								? files => {
										setFilesToSend(files);
										params.setActiveStepPath('temporaryStorage');
								  }
								: undefined
						}
					/>
				),
			});
		}

		if (isAllowed('patient_health_ezdravie')) {
			tempSteps.push({
				name: 'patientTreatment.ezk.title',
				key: 'ezk',
				component: (params: NavigationStepperComponentParams) => (
					<PatientTreatmentElectronicHealthCard
						patientId={patientId}
						setIsLoading={params.setIsLoading}
					/>
				),
				icon: '/dist/icons/eZdravie.png',
			});
			tempSteps.push({
				name: 'patientTreatment.electronicExamination.title',
				key: 'electronicExamination',
				component: (params: NavigationStepperComponentParams) => (
					<ElectronicExamination
						patientId={patientId}
						setIsLoading={params.setIsLoading}
					/>
				),
				icon: '/dist/icons/eZdravie.png',
			});
			tempSteps.push({
				name: 'patientTreatment.electronicVaccination.title',
				key: 'electronicVaccination',
				component: (params: NavigationStepperComponentParams) => (
					<ElectronicVaccination
						patientId={patientId}
						setIsLoading={params.setIsLoading}
					/>
				),
				icon: '/dist/icons/eZdravie.png',
			});
			tempSteps.push({
				name: 'patientTreatment.electronicDPN.title',
				key: 'electronicDPN',
				component: (params: NavigationStepperComponentParams) => (
					<ElectronicDPN setIsLoading={params.setIsLoading} />
				),
				icon: '/dist/icons/eZdravie.png',
			});
			tempSteps.push({
				name: 'patientTreatment.electronicPrescription.title',
				key: 'electronicPrescription',
				component: (params: NavigationStepperComponentParams) => (
					<ElectronicPrescription
						setIsLoading={params.setIsLoading}
						workplaceId={workplaceId}
					/>
				),
				icon: '/dist/icons/eZdravie.png',
			});
		} else if (isAllowed('ezdravie_setup')) {
			tempSteps.push({
				name: 'component.nczi.setup.button',
				key: 'ncziSetup',
				component: (params: NavigationStepperComponentParams) => (
					<ScrollContainer>
						<div style={{ padding: '14px' }}>
							<NCZIWizard
								onFinish={() => {
									dispatch(setAclLoading(false));
									dispatch(setAclLoaded(false));
								}}
								showFinishButton
							/>
						</div>
					</ScrollContainer>
				),
				icon: '/dist/icons/eZdravie.png',
			});
		}

		const rows = data?.workplaceHealthPlans?.data || [];

		if (rows.length) {
			tempSteps.push({
				name: 'patientTreatment.healthPlans.title',
				key: 'healthPlans',
				component: (params: NavigationStepperComponentParams) => (
					<PatientTreatmentHealthPlan
						healthPlans={rows}
						workplaceId={workplaceId}
						activeTabParams={activeTabParams}
					/>
				),
			});
		}

		setSteps(tempSteps);
	}, [
		isNurse,
		data,
		patientsOriginalId,
		activeTabParams,
		mzcrAccess.isVisible,
		mzcrAccess.hasServices,
	]);

	const initialStep: string = useMemo(() => {
		let temp =
			activeTab ??
			getActiveTreatmentTabForPatient(patientId, workplaceId) ??
			'profile';

		//CHECK IF INITIAL STEP IS AVAILABLE IF NOT GOES WITH PROFILE, BC USER EVERY TIME HAVE PROFILE AT LEAST
		if (!steps.some(step => step.key === temp)) {
			temp = 'profile';
		}
		return temp;
	}, [steps]);

	if (!steps.length) {
		return <></>;
	}

	const handleChangeStep = (stepPath: string) => {
		setActiveTreatmentTabForPatient(stepPath, patientId, workplaceId);
	};

	if (isLoading || isMyCaseIsLoading) {
		return <Loading />;
	}

	return (
		<PatientTreatmentContextProvider
			patientId={patientId}
			caseId={caseId}
			isMyCase={isMyCase}
			filesToSend={filesToSend}
			onFilesToSendTaken={() => setFilesToSend(null)}>
			<NCZICommandContextProvider>
				<NCZIContextProvider>
					<PatientDataContextProvider>
						<TreatmentContextProvider
							values={{
								isTreatment: true,
								isCase: !!caseId,
								openMeddiDiabetes: false,
								openMedicalReport: false,
								setOpenMedicalReport: () => {},
								setOpenMeddiDiabetes: () => {},
								setIsTreatment: () => {},
								setIsCase: () => {},
								workplaceId,
							}}>
							<TabNavigationStepper
								steps={steps}
								initialStepPath={initialStep}
								canSkip={true}
								onStepChange={handleChangeStep}
								fallbackStep='profile'
							/>
							<PatientAutoVerify justAssigned={caseJustAssigned} />
						</TreatmentContextProvider>
					</PatientDataContextProvider>
				</NCZIContextProvider>
			</NCZICommandContextProvider>
		</PatientTreatmentContextProvider>
	);
};

export default PatientTreatment;
